import React from 'react';

// Generic colored pill — reusable across the app for any "level / category"
// hint. The training-zone variant (ZoneBadge) is a thin wrapper that picks
// the right color per zone. Other call-sites can pass arbitrary colors:
//   • Profile fields  — "מתחיל" / "בינוני" / "מתקדם"
//   • Race lists      — "מרוץ מטרה" (yellow) / "אימון" (cyan)
//   • Workout types   — "טמפו" (purple), "אינטרוולים" (red)
//
// Palette here MUST mirror `components.badge.*` in src/styles/design-tokens.js
// (the visual-style source of truth). Update both when adding a new color.
//
// Props:
//   label — required, the text shown inside the badge
//   color — one of the keyed palette below OR a custom {bg, border, text} object
//   size  — 'lg' (default) | 'sm' (used in compact rows)
//   icon  — optional leading icon (string emoji)
//   title — html title for hover tooltip

const PALETTE = {
  green: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  emerald: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  teal: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  cyan: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  blue: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  purple: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  pink: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  rose: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  red: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  orange: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  amber: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  yellow: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  slate: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
  gray: { bg: 'bg-pressed', border: 'border-border', text: 'text-text-body' },
};

const DEFAULT_COLOR = 'blue';

export default function IntensityBadge({
  label,
  color = DEFAULT_COLOR,
  size = 'lg',
  icon,
  title,
  className = '',
}) {
  if (!label) return null;

  const palette = typeof color === 'string'
    ? (PALETTE[color] || PALETTE[DEFAULT_COLOR])
    : color;

  const sizing = size === 'sm'
    ? 'px-2 py-0.5 text-[10px]'
    : 'px-3 py-1 text-xs';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-bold ${sizing} ${palette.bg} ${palette.border} ${palette.text} ${className}`}
      title={title || label}
    >
      {icon && <span>{icon}</span>}
      <span>{label}</span>
    </span>
  );
}

// Export the palette so consumers can name colors instead of guessing.
export const INTENSITY_COLORS = Object.keys(PALETTE);
